import {FC, useEffect, useMemo, useState} from 'react';
import {styled} from 'styled-components';

import {groupBy} from '@shared/lib/utils/array_utils';
import {isEmpty} from '@shared/lib/utils/object_utils';
import {GroupType, SupportGroupMember} from '@shared/models/api_models';

import {NavLink} from '@shared_web/components/core/button';
import {LoadingIndicator} from '@shared_web/components/core/loading_indicator';
import {SvgIcon} from '@shared_web/components/core/svg_icon';
import {Flex} from '@shared_web/components/core2/flex';
import {notifyError} from '@shared_web/lib/notification';
import {textColor} from '@shared_web/theme/theme_utils';

import {CollapsableSection} from '@mavismart_frontend/components/collapsable_section';
import {Loader} from '@mavismart_frontend/components/loader';
import {Translate} from '@mavismart_frontend/components/translate';
import {icon_door_specifications} from '@mavismart_frontend/icons/icon_door_specifications';
import {icon_home} from '@mavismart_frontend/icons/icon_home';
import {apiCall} from '@mavismart_frontend/lib/api';
import {MAIN_COLOR_DARK, TEXT_MAIN_COLOR} from '@mavismart_frontend/lib/constants';
import {GenericInfoBlock} from '@mavismart_frontend/pages/cells/generic_info_block';
import {useAccount} from '@mavismart_frontend/stores/account_store';
import {fetchDoor, useDoor} from '@mavismart_frontend/stores/all_doors_store';

export type DisplayType = 'anomalies' | 'uses' | 'alerts' | 'messages' | 'editMessage';
type DisplayTypeWithoutMessages = Exclude<DisplayType, 'messages' | 'editMessage'>;
export const TITLE_DISPLAYED_TYPES: DisplayTypeWithoutMessages[] = ['uses', 'anomalies', 'alerts'];

interface DoorDetailsProps {
  doorId: string;
  baseUrl: string;
}

export const DoorDetails: FC<DoorDetailsProps> = props => {
  const {doorId} = props;
  const account = useAccount();
  const supportAppEnabled = useMemo(
    () => account?.hasSupportApp === true && account?.supportAppEnabled === true,
    [account]
  );

  //
  // Fetch the door groups
  //
  const door = useDoor(doorId);
  useEffect(() => fetchDoor(doorId), [doorId]);

  //
  // Fetch the door members
  //
  const [doorMembers, setDoorMembers] = useState<SupportGroupMember[] | undefined>();
  useEffect(() => {
    if (!supportAppEnabled) {
      setDoorMembers(undefined);
      return;
    }
    apiCall('POST /support-list-door-emails', {doorId})
      .then(({groupMembers}) => setDoorMembers(groupMembers))
      .catch(notifyError);
  }, [doorId, supportAppEnabled]);

  const doorEmails = useMemo(() => {
    if (!doorMembers) {
      return undefined;
    }
    return [...groupBy(doorMembers, m => m.email).keys()].sort();
  }, [doorMembers]);

  //
  // Render the door groups list
  //
  const doorGroupsList = useMemo(() => {
    if (!door || door?.directGroups.length === 0) {
      return <LoadingIndicator />;
    }

    return (
      <CollapsableSection
        header={
          <Flex $gap="s">
            <Translate value="door_details__door_groups" />
            {` (${door?.directGroups.length ?? '...'})`}
          </Flex>
        }
        isOpen={door?.directGroups !== undefined}
      >
        <Flex $column $gap="m" $margin={[0, 0, 's', 0]}>
          {door?.directGroups.map(g => {
            const {location, contact, contact2, info} = g;
            const contactBlock =
              contact || contact2 ? (
                <GenericInfoBlock
                  icon={<SvgIcon icon={icon_door_specifications} size={22} />}
                  items={[
                    {
                      key: 'contacts',
                      title: <Translate value="contacts" />,
                      subtitle: (
                        <>
                          {contact !== undefined &&
                            contact2 !== undefined &&
                            isEmpty(contact) &&
                            isEmpty(contact2) && <div>-</div>}
                          {contact && (
                            <div>
                              <div>
                                {contact.contactFirstName ? contact.contactFirstName : <></>}{' '}
                                {contact.contactLastName ? contact.contactLastName : <></>}{' '}
                              </div>
                              <div>{contact.contactPhone ? contact.contactPhone : <></>}</div>
                              <div>{contact.contactEmail ? contact.contactEmail : <></>}</div>
                            </div>
                          )}
                          {contact2 && (
                            <div>
                              <div>
                                {contact2.contactFirstName ? contact2.contactFirstName : <></>}{' '}
                                {contact2.contactLastName ? contact2.contactLastName : <></>}{' '}
                              </div>
                              <div>{contact2.contactPhone ? contact2.contactPhone : <></>}</div>
                              <div>{contact2.contactEmail ? contact2.contactEmail : <></>}</div>
                            </div>
                          )}
                        </>
                      ),
                    },
                  ]}
                />
              ) : undefined;

            const addressBlock = location ? (
              <GenericInfoBlock
                icon={<SvgIcon icon={icon_home} size={22} color="transparent" />}
                items={[
                  {
                    key: 'fulladdress',
                    title: <Translate value="address" />,
                    subtitle: (
                      <div>
                        <div>
                          {location.address1 ? location.address1 : <></>}{' '}
                          {location.address2 ? location.address2 : <></>}{' '}
                          {location.address3 ? location.address3 : <></>}
                        </div>
                        <div>
                          {location.postalCode} {location.city}
                        </div>
                        <div>{location.country}</div>
                      </div>
                    ),
                  },
                ]}
              />
            ) : undefined;

            const keys: (typeof g.from)[number][] = [];
            if (g.from.includes('buyer')) {
              keys.push('buyer');
            }
            if (g.from.includes('maintainer')) {
              keys.push('maintainer');
            }
            if (g.from.includes('customer')) {
              keys.push('customer');
            }
            const translationKey = `door_${keys.join('_and_')}` as
              | 'door_buyer'
              | 'door_buyer_and_maintainer'
              | 'door_buyer_and_maintainer_and_customer'
              | 'door_buyer_and_customer'
              | 'door_maintainer'
              | 'door_maintainer_and_customer'
              | 'door_customer';

            if (!info) {
              return <></>;
            }

            const title = `${info.name} (${info.id})`;

            return (
              <Flex $column key={info.id}>
                {supportAppEnabled ? (
                  <StyledNavLink
                    to={`/groups/${info.id}`}
                    themeOverrides={{
                      ...textColor(MAIN_COLOR_DARK),
                      fontWeight: '600',
                    }}
                  >
                    {title}
                  </StyledNavLink>
                ) : (
                  <Title>{title}</Title>
                )}
                <GroupSubtitle>
                  <Translate value={translationKey} />
                </GroupSubtitle>
                {info.type !== GroupType.Manual ? (
                  <>
                    {addressBlock}
                    {contactBlock}
                  </>
                ) : (
                  <></>
                )}
              </Flex>
            );
          })}
        </Flex>
      </CollapsableSection>
    );
  }, [door, supportAppEnabled]);

  //
  // Render the door members list
  //
  const doorMembersList = useMemo(() => {
    if (!supportAppEnabled) {
      return <></>;
    }
    return (
      <CollapsableSection
        header={
          <Flex $gap="s">
            <Translate value="door_details__door_members" />
            {doorEmails?.length ? ` (${doorEmails.length})` : ''}
          </Flex>
        }
        isOpen={doorEmails !== undefined}
      >
        {doorEmails !== undefined && (
          <DoorMemberLine>
            {doorEmails.map(email => {
              return <DoorMemberLine key={email}>{email}</DoorMemberLine>;
            })}
          </DoorMemberLine>
        )}
      </CollapsableSection>
    );
  }, [doorEmails, supportAppEnabled]);

  if (door === undefined) {
    return <Loader />;
  }

  return (
    <MainContent $column $gap="l">
      {doorGroupsList}
      {doorMembersList}
    </MainContent>
  );
};

DoorDetails.displayName = 'DoorDetails';

const MainContent = styled(Flex)`
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
  overflow-x: hidden;
`;

const DoorMemberLine = styled.div`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const GroupSubtitle = styled.div`
  font-size: 12px;
  color: ${TEXT_MAIN_COLOR}aa;
`;

const StyledNavLink = styled(NavLink)`
  line-height: 100%;
  margin-top: 4px;
`;

const Title = styled.div`
  color: ${TEXT_MAIN_COLOR};
  font-weight: 600;
  line-height: 20px;
`;
